import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { ASSET_TYPES } from '../video-state';
import { isNonRecommendable } from '../video-state/serialization/assetSerializer';

import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { setAnimationEnabledOperation } from 'js/src/luminary/luminary-operations';
import { getSceneDesignAnimations } from 'js/src/luminary/luminary-selectors';

export const setTextAnimationSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe(`The ${ASSET_TYPES.TEXT} asset name (e.g. text_1)`),
    animate: z
        .boolean()
        .describe(
            `Whether the text plays its animation. false disables it so the text appears statically; true restores the scene design's animation. Any custom appearance timing set with ${TOOL_NAMES.setText} is preserved either way.`,
        ),
});
export type SetTextAnimationArgs = z.infer<typeof setTextAnimationSchema>;

export class SetTextAnimationTool extends VideoStateTool<typeof setTextAnimationSchema> {
    protected override readonly schema = setTextAnimationSchema;
    protected override readonly toolName = TOOL_NAMES.setTextAnimation;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription = `Turn a ${ASSET_TYPES.TEXT} asset's animation on or off. Disabling makes the text appear statically instead of playing the scene design's entrance/exit animation; any custom appearance timing set with ${TOOL_NAMES.setText} is preserved. Address the scene by its stable scene_id. Only affects ${ASSET_TYPES.TEXT} assets the design actually animates.`;

    public override getToolDisplayName(): string {
        return 'Setting text animation';
    }

    public get statusIcon(): string {
        return 'animation';
    }

    public async execute(args: SetTextAnimationArgs, context: ToolExecutionContext) {
        const { asset_name, animate } = args;
        const { parentActionGroup } = context;

        try {
            const { sceneIndex, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[sceneIndex];
            if (!scene) {
                return `Error: No scene found for scene_id ${sceneShortId}`;
            }

            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId}`;
            }
            if (!VideoData.isTextAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not a ${ASSET_TYPES.TEXT} asset`;
            }
            if (!asset.isEditable || isNonRecommendable(scene, asset_name, this.getState())) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not editable`;
            }

            // Only scene-design animations are the on/off target — a design that doesn't animate this
            // text has nothing to disable, so say so rather than reporting a no-op success.
            const sceneDesignAnimations = getSceneDesignAnimations(asset);
            if (sceneDesignAnimations.length === 0) {
                return `Error: ${asset_name} in scene ${sceneShortId} has no animation to change.`;
            }

            const currentlyAnimated = sceneDesignAnimations.some((animation) => animation.enabled);
            if (currentlyAnimated === animate) {
                return `No change: animation is already ${animate ? 'on' : 'off'} for ${asset_name} in scene ${sceneShortId}.`;
            }

            const { setAnimationEnabled } = makeOperationsCallable(
                { setAnimationEnabledOperation },
                this.luminary,
                this.dispatch,
                () => this.getState(),
            );
            setAnimationEnabled(scene.id, asset.id, animate, parentActionGroup);

            return `Success: Turned animation ${animate ? 'on' : 'off'} for ${asset_name} in scene ${sceneShortId}.`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting text animation: ${errorMessage}`;
        }
    }
}
